import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { ArrowLeft, Gift } from "@phosphor-icons/react";

const LOGO_URL =
  "/vznlogo.png";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = (searchParams.get("ref") || "").trim().toUpperCase();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [referrerName, setReferrerName] = useState(null);

  // Resolve the referrer's display name (silent — invalid codes simply won't show a banner)
  useEffect(() => {
    if (!refCode) return;
    let cancelled = false;
    api
      .get(`/referrals/lookup/${encodeURIComponent(refCode)}`)
      .then(({ data }) => {
        if (!cancelled && data?.valid) setReferrerName(data.referrer_name);
      })
      .catch(() => {
        /* invalid or expired code — show nothing */
      });
    return () => {
      cancelled = true;
    };
  }, [refCode]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await register(email, password, name, companyName, refCode || null);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setErr(res.error);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 md:p-12 order-2 lg:order-1">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="register-form">
          <div className="lg:hidden mb-8">
            <Link to="/" className="inline-flex w-full">
              <img src={LOGO_URL} alt="Vision Grafix" className="w-full h-auto object-contain" />
            </Link>
          </div>
          <h2 className="font-display text-4xl mb-2 uppercase">Create account</h2>
          <p className="text-sm text-[#a3a3a3] mb-8">It takes ten seconds.</p>

          {referrerName && (
            <div
              className="mb-6 p-4 border border-[#FF2D2D]/40 bg-gradient-to-r from-[#FF2D2D]/10 to-transparent flex items-center gap-3"
              data-testid="referral-banner"
            >
              <Gift size={20} className="text-[#FF2D2D] shrink-0" weight="fill" />
              <div className="text-sm">
                Referred by <span className="font-semibold">{referrerName}</span>. Welcome aboard.
              </div>
            </div>
          )}

          {err && (
            <div className="mb-4 p-3 border border-[#ff2a00] bg-[#ff2a00]/10 text-sm text-[#ff8866]" data-testid="register-error">
              {err}
            </div>
          )}

          <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Full name</label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="register-name"
            className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 text-white focus:border-[#FF2D2D] outline-none"
          />

          <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-5 mb-2">Company / organization <span className="text-[#666] normal-case tracking-normal">(optional)</span></label>
          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            data-testid="register-company"
            className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 text-white focus:border-[#FF2D2D] outline-none"
          />

          <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-5 mb-2">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="register-email"
            className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 text-white focus:border-[#FF2D2D] outline-none"
          />

          <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-5 mb-2">Password</label>
          <input
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="register-password"
            className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 text-white focus:border-[#FF2D2D] outline-none"
          />
          <div className="text-xs text-[#a3a3a3] mt-1">Min 6 characters.</div>

          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit"
            className="btn-primary w-full mt-8 px-6 py-3 text-sm font-bold uppercase tracking-wider"
          >
            {loading ? "Creating…" : "Create account →"}
          </button>

          <div className="mt-6 text-sm text-[#a3a3a3]">
            Already have one?{" "}
            <Link to="/login" className="text-[#FF2D2D] hover:underline" data-testid="register-login-link">
              Sign in
            </Link>
          </div>
        </form>
      </div>
      <div className="hidden lg:block relative border-l border-[#262626] order-1 lg:order-2 mesh-overlay">
        <img
          src="/register-bg.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-10 h-full p-12 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-white/80 hover:text-white" data-testid="back-home-2">
            <ArrowLeft size={16} /> Back home
          </Link>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.4em] text-[#FF2D2D] mb-3">Join the portal</div>
            <h1 className="font-display text-7xl leading-[0.9] text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]">Share your<br />vision.<br />We'll build it.</h1>
          </div>
          <div className="text-xs text-white/70 uppercase tracking-[0.2em]">Vision Grafix — Client Portal</div>
        </div>
      </div>
    </div>
  );
}
